import { supabase } from '../lib/supabase';
import { GeminiService } from './geminiService';
import JobBoardService from './jobBoardService';
import { JOB_ROLES, getRolesByCategory } from '../data/jobRoles';
import ComparisonService from './comparisonService';

// Browser-compatible PDF parser
import * as pdfjsLib from 'pdfjs-dist';

// DOCX parser
import mammoth from 'mammoth';

// Import worker as URL (Vite will bundle it)
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Service for handling resume analysis operations
 */
export class ResumeAnalysisService {
  /**
   * Uploads resume file to Supabase Storage
   */
  static async uploadResumeFile(file, userId) {
    const fileName = `${userId}/${Date.now()}_${file.name}`;

    const { data, error } = await supabase.storage
      .from('resumes')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      throw new Error('Failed to upload resume file');
    }

    return data.path;
  }

  /**
   * FALLBACK: Simulates text extraction for testing/demo purposes
   * Used when PDF parsing fails or for quick testing
   */
  static simulateTextExtraction(fileName) {
    console.warn('⚠️ Using simulated text extraction. Real PDF parsing failed.');
    return `John Doe
Software Developer
Email: john.doe@email.com
Phone: +91-9999999999
Location: Bangalore, India

SUMMARY
Experienced software developer with 5+ years in full-stack development.
Proficient in JavaScript, React, Node.js, Python, and cloud technologies.
Strong background in agile development and team collaboration.

TECHNICAL SKILLS
Languages: JavaScript, Python, Java, TypeScript
Frontend: React, Vue.js, Angular, HTML5, CSS3
Backend: Node.js, Express.js, Django, Flask
Database: MongoDB, PostgreSQL, MySQL
Cloud: AWS, Google Cloud, Azure
Tools: Git, Docker, Kubernetes, Jenkins

EXPERIENCE
Senior Software Developer | Tech Company | 2020 - Present
- Developed and maintained web applications using React and Node.js
- Implemented RESTful APIs and microservices architecture
- Collaborated with cross-functional teams in agile environment
- Improved application performance by 40% through optimization

Software Developer | Previous Company | 2019 - 2020
- Built responsive web applications using modern JavaScript frameworks
- Integrated third-party APIs and payment gateways
- Participated in code reviews and mentored junior developers

EDUCATION
Bachelor of Technology in Computer Science
Indian Institute of Technology | 2015 - 2019

PROJECTS
E-commerce Platform - Full-stack web application with payment integration
Task Management System - React-based project management tool
API Gateway - Microservices architecture with Docker and Kubernetes

CERTIFICATIONS
AWS Certified Solutions Architect
Google Cloud Professional Developer`;
  }

  /**
   * Extracts text content from resume file - BROWSER-COMPATIBLE VERSION
   */
  static async extractTextFromFile(file) {
    try {
      if (file.type === 'application/pdf') {
        console.log('📄 Parsing PDF with pdfjs-dist (browser)...');

        try {
          // Read file as ArrayBuffer
          const arrayBuffer = await file.arrayBuffer();

          // Load PDF document
          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;

          console.log(`📄 PDF loaded: ${pdf.numPages} pages`);

          // Extract text from all pages
          let fullText = '';

          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n';
          }

          if (!fullText || fullText.trim().length < 50) {
            console.warn('⚠️ Insufficient text extracted from PDF. Using simulation.');
            return this.simulateTextExtraction(file.name);
          }


          return fullText.trim();
        } catch (pdfError) {
          console.error('❌ PDF parsing error:', pdfError);
          console.log('🔄 Falling back to simulated text extraction');
          return this.simulateTextExtraction(file.name);
        }

      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) {
        // DOCX files


        try {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          const text = result.value;

          if (!text || text.trim().length < 50) {
            console.warn('⚠️ Insufficient text extracted from DOCX. Using simulation.');
            return this.simulateTextExtraction(file.name);
          }


          return text.trim();
        } catch (docxError) {
          console.error('❌ DOCX parsing error:', docxError);
          console.log('🔄 Falling back to simulated text extraction');
          return this.simulateTextExtraction(file.name);
        }

      } else if (file.type.includes('text')) {
        // Plain text files
        const text = await file.text();
        return text;

      } else {
        console.warn('⚠️ Unsupported file type. Using simulation.');
        return this.simulateTextExtraction(file.name);
      }
    } catch (error) {
      console.error('❌ File extraction error:', error);
      console.log('🔄 Falling back to simulated text extraction');
      return this.simulateTextExtraction(file.name);
    }
  }

  /**
   * Performs AI-powered resume analysis
   */
  static async analyzeResumeWithAI(resumeText, userTier = 'free', preferredRoles = [], experienceYears = 0) {
    try {
      console.log('🤖 Starting AI analysis...');
      const analysis = await GeminiService.analyzeResume(
        resumeText,
        userTier,
        preferredRoles,
        experienceYears
      );

      console.log('✅ AI analysis complete');
      return analysis;
    } catch (error) {
      console.error('❌ AI analysis error:', error);
      throw new Error('AI analysis failed. Please try again.');
    }
  }

  /**
   * Stores analysis results in database with REAL job matching
   */
  static async storeAnalysisResults(userId, analysisData) {
    try {
      // Insert resume analysis
      const { data: analysis, error: analysisError } = await supabase
        .from('resume_analyses')
        .insert({
          user_id: userId,
          file_name: 'live_analysis',
          file_path: 'live',
          file_size: 0,
          status: 'completed',
          overall_score: analysisData?.analysis?.overall_resume_score || 0,
          job_match_score: analysisData?.analysis?.job_match_score ?? null, // <-- add
          analysis_results: analysisData,
          processing_completed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (analysisError) throw analysisError;

      const analysisId = analysis.id;

      // Store skills
      if (Array.isArray(analysisData.skills) && analysisData.skills.length > 0) {
        const skillsToInsert = analysisData.skills.map(skill => ({
          resume_analysis_id: analysisId,
          skill_name: skill.name,
          skill_category: skill.category || 'technical',
          category: skill.category || 'technical',
          proficiency_level: skill.proficiency || 'intermediate',
          years_experience: Math.floor(parseFloat(skill.years_experience) || 0),
          is_technical: skill.is_technical !== undefined ? skill.is_technical : true
        }));

        const { error: skillsError } = await supabase
          .from('resume_skills')
          .insert(skillsToInsert);

        if (skillsError) {
          console.error('Skills insert error:', skillsError);
        }
      }

      // ✅ Match against REAL jobs in database (not AI-generated)
      console.log('🔍 Matching resume against existing jobs in database...');
      await this.matchResumeToExistingJobs(
        analysisId,
        analysisData.skills,
        analysisData.targetRole
      );

      // ✅ FIX: Reload complete analysis WITH job_matches
      console.log('🔄 Reloading complete analysis with linked jobs...');
      const completeAnalysis = await this.getAnalysisById(analysisId);

      // ✅ NEW: Calculate Job Match Score based on real job data
      const matchingSkills = completeAnalysis.skills_match?.length || 0;
      const missingSkills = completeAnalysis.missing_skills?.length || 0;
      const totalRequiredSkills = matchingSkills + missingSkills;

      const jobMatchScore = totalRequiredSkills > 0
        ? Math.round((matchingSkills / totalRequiredSkills) * 100)
        : 0;

      console.log(`📊 Job Match Score: ${jobMatchScore}% (${matchingSkills} matching / ${totalRequiredSkills} required)`);

      // Update the analysis with job match score
      const { error: updateError } = await supabase
        .from('resume_analyses')
        .update({
          job_match_score: jobMatchScore
        })
        .eq('id', analysisId);

      if (updateError) {
        console.error('Failed to update job match score:', updateError);
      }

      // Add job match score to returned data
      completeAnalysis.job_match_score = jobMatchScore;

      // 🆕 COMPARISON: Track improvements if this is a re-scan
      if (analysisData.targetRole) {
        await ComparisonService.storeComparisonIfAvailable(
          userId,
          analysisId,
          analysisData.targetRole,
          completeAnalysis
        );
      }

      return completeAnalysis;

    } catch (error) {
      console.error('❌ Database storage error:', error);
      throw new Error('Failed to store analysis results');
    }
  }

  /**
   * Match resume against REAL jobs already in database
   * Uses category-based filtering for better relevance
   * NO API CALLS - uses pre-populated job database from cron jobs
   */
  static async matchResumeToExistingJobs(analysisId, userSkills, targetRole, limit = 20) {
    try {
      const userSkillNames = userSkills.map(s => s.name.toLowerCase());

      console.log(`📊 User has ${userSkills.length} skills:`, userSkillNames.slice(0, 5));
      console.log(`🎯 Target Role: "${targetRole}"`);

      // Find the category of the target role
      const selectedRole = JOB_ROLES.find(role => role.title === targetRole);
      const categoryId = selectedRole?.category;

      console.log(`📦 Category: "${categoryId}"`);

      // Get ALL roles in this category
      const rolesInCategory = categoryId
        ? getRolesByCategory(categoryId).map(r => r.title)
        : [targetRole];

      console.log(`📦 Matching against ${rolesInCategory.length} roles in category`);

      // Query jobs matching any role in the category (partial match)
      let query = supabase
        .from('job_matches')
        .select('*')
        .eq('is_active', true)
        .is('resume_analysis_id', null);

      // Build partial match for all roles in category
      const roleFilters = rolesInCategory.map(role =>
        `job_title.ilike.%${role}%`
      ).join(',');

      if (roleFilters) {
        query = query.or(roleFilters);
      }

      const { data: existingJobs, error } = await query.limit(100);

      if (error) {
        console.error('Job query error:', error);
        return;
      }

      if (!existingJobs || existingJobs.length === 0) {
        console.warn(`⚠️ No jobs found for category "${categoryId}"`);
        return;
      }

      console.log(`📦 Found ${existingJobs.length} jobs to match against`);

      // Calculate match percentage for each job
      const matchedJobs = existingJobs.map(job => {
        const jobSkills = job.normalized_skills && job.normalized_skills.length > 0
          ? job.normalized_skills
          : [...(job.matching_skills || []), ...(job.requirements || [])].map(s => s.toLowerCase());

        const matchingSkills = userSkillNames.filter(userSkill =>
          jobSkills.some(jobSkill =>
            userSkill.includes(jobSkill) ||
            jobSkill.includes(userSkill) ||
            userSkill === jobSkill
          )
        );

        const matchPercentage = jobSkills.length > 0
          ? Math.round((matchingSkills.length / jobSkills.length) * 100)
          : 0;

        const missingSkills = jobSkills.filter(jobSkill =>
          !userSkillNames.some(userSkill =>
            userSkill.includes(jobSkill) || jobSkill.includes(userSkill)
          )
        );

        return {
          jobId: job.id,
          matchPercentage,
          matchingSkills,
          missingSkills: missingSkills.slice(0, 10),
          matchRange: this.getMatchRange(matchPercentage)
        };
      });

      // Sort by match percentage and get top matches
      const topMatches = matchedJobs
        .sort((a, b) => b.matchPercentage - a.matchPercentage)
        .slice(0, limit);

      console.log(`✅ Top match: ${topMatches[0]?.matchPercentage}% | Lowest: ${topMatches[topMatches.length - 1]?.matchPercentage}%`);

      // Link top matching jobs to this resume analysis
      for (const match of topMatches) {
        await supabase
          .from('job_matches')
          .update({
            resume_analysis_id: analysisId,
            match_percentage: match.matchPercentage,
            match_range: match.matchRange,
            matching_skills: match.matchingSkills,
            missing_skills: match.missingSkills
          })
          .eq('id', match.jobId);
      }

      console.log(`🔗 Linked ${topMatches.length} jobs to resume analysis ${analysisId}`);

    } catch (error) {
      console.error('❌ Job matching error:', error);
    }
  }

  /**
   * Helper: Get match range from percentage
   */
  static getMatchRange(percentage) {
    if (percentage >= 80) return 'excellent';
    if (percentage >= 60) return 'high';
    if (percentage >= 40) return 'medium';
    return 'low';
  }

  /**
   * Main method: Complete resume analysis workflow
   */
  static async performCompleteAnalysis(file, userId, targetRole, preferredRoles = [], experienceYears = 0, userTier = 'free') {
    try {
      console.log('🚀 Starting complete resume analysis...');

      // Step 1: Upload file
      console.log('📤 Uploading file...');
      const filePath = await this.uploadResumeFile(file, userId);

      // Step 2: Extract text
      console.log('📄 Extracting text...');
      const resumeText = await this.extractTextFromFile(file);

      // Step 3: AI Analysis (skills extraction only, NO job generation)
      console.log('🤖 Running AI analysis...');
      const aiAnalysis = await this.analyzeResumeWithAI(
        resumeText,
        userTier,
        preferredRoles,
        experienceYears
      );

      // Step 4: Store results (includes job matching against DB)
      console.log('💾 Storing results...');
      const analysisResult = await this.storeAnalysisResults(userId, {
        fileName: file.name,
        filePath,
        targetRole,
        overall_score: aiAnalysis.overall_score,
        skills: aiAnalysis.skills,
        feedback: aiAnalysis.feedback,
        // Store ALL AI analysis data
        fullAnalysis: aiAnalysis
      });

      console.log('✅ Analysis complete!');
      return analysisResult;
    } catch (error) {
      console.error('❌ Complete analysis error:', error);
      throw error;
    }
  }

  /**
   * Retrieves a specific analysis by ID
   */
  static async getAnalysisById(analysisId) {
    try {
      const { data, error } = await supabase
        .from('resume_analyses')
        .select(`
          *,
          resume_skills (*),
          job_matches (*)
        `)
        .eq('id', analysisId)
        .single();

      if (error) throw error;

      // DEBUG
      console.log('🔍 Raw data from DB:', {
        has_job_matches: !!data?.job_matches,
        job_matches_count: data?.job_matches?.length || 0,
        job_matches_sample: data?.job_matches?.[0],
        all_keys: Object.keys(data || {})
      });

      // Merge analysis_results: handle LIVE structure first, else legacy
      if (data && data.analysis_results) {
        // NEW live-analysis format from /analysis-results-live
        if (data.analysis_results.analysis) {
          const live = data.analysis_results.analysis;
          const posting = data.analysis_results.jobPosting || {};

          const skillsFound = Array.isArray(live.skills_found) ? live.skills_found : [];
          const skillsMissing = Array.isArray(live.skills_missing) ? live.skills_missing : [];
          const skillsMatching = Array.isArray(live.skills_matching) ? live.skills_matching : [];

          return {
            ...data,
            role: posting.title || data.target_role || 'Unknown Role',
            overall_score: live.overall_resume_score || data.overall_score || 0,
            job_match_score: live.job_match_score || 0,
            extracted_skills: skillsFound.map(s => typeof s === 'string' ? { name: s } : s),
            skills_match: skillsMatching,
            missing_skills: skillsMissing.map(g => g?.name || g).filter(Boolean),
            job_matches: [{
              id: 'live-match',
              job_title: posting.title || 'Unknown',
              company_name: posting.company || 'Unknown',
              match_percentage: live.job_match_score || 0,
              job_description: posting.summary || '',
              location: posting.location || 'India',
              job_type: 'full-time',
              external_url: posting.job_url || ''
            }],
            resume_keywords: skillsFound.map(s => typeof s === 'string' ? s : (s.name || s.skill_name)).filter(Boolean),
            ats_keywords: skillsMatching,
            analysis_summary: `Live analysis for ${posting.title || 'your resume'} - Score: ${live.overall_resume_score || 0}%`,
            isLiveAnalysis: true,
            liveAnalysisData: { analysis: live, jobPosting: posting }
          };
        }

        // LEGACY format (existing logic)
        const allMissingSkills = [];
        const allMatchingSkills = [];

        if (Array.isArray(data.job_matches)) {
          data.job_matches.forEach(match => {
            if (Array.isArray(match.missing_skills)) {
              allMissingSkills.push(...match.missing_skills);
            }
            if (Array.isArray(match.matching_skills)) {
              allMatchingSkills.push(...match.matching_skills);
            }
          });
        }

        const allImprovements = [];
        if (Array.isArray(data.analysis_results.feedback)) {
          data.analysis_results.feedback.forEach(fb => {
            if (Array.isArray(fb.improvement_suggestions)) {
              allImprovements.push(...fb.improvement_suggestions);
            }
          });
        }

        return {
          ...data,
          ...data.analysis_results,
          role: data.target_role,
          extracted_skills: data.resume_skills || data.analysis_results.skills || [],
          job_matches: data.job_matches || [],
          skills_match: allMatchingSkills.filter((v, i, a) => a.indexOf(v) === i),
          missing_skills: allMissingSkills.filter((v, i, a) => a.indexOf(v) === i),
          improvement_suggestions: allImprovements.filter((v, i, a) => a.indexOf(v) === i),
          ats_keywords: allMatchingSkills.slice(0, 10) || [],
          resume_keywords: (data.analysis_results.skills || []).map(s => s.name).slice(0, 15),
          analysis_summary: `Analysis for ${data.target_role || 'your resume'} - Score: ${data.overall_score || 0}%`
        };
      }

      return data;
    } catch (error) {
      console.error('❌ Get analysis error:', error);
      throw new Error('Failed to retrieve analysis');
    }
  }

  /**
   * Retrieves all analyses for a user
   */
  static async getUserAnalyses(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('resume_analyses')
        .select('id, created_at, status, file_name, overall_score, job_match_score, analysis_results, previous_scan_id, target_role')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit || 10);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Get user analyses error:', error);
      throw new Error('Failed to retrieve user analyses');
    }
  }

  /**
   * Checks if user has remaining free credits
   */
  static async checkUserCredits(userId) {
    try {
      // Get user's tier
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('tier')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // If premium/enterprise, unlimited
      if (profile.tier !== 'free') {
        return { hasCredits: true, remaining: 'unlimited' };
      }

      // For free tier, count analyses
      const { count, error: countError } = await supabase
        .from('resume_analyses')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (countError) throw countError;

      const FREE_TIER_LIMIT = 1;
      const remaining = Math.max(0, FREE_TIER_LIMIT - (count || 0));

      return {
        hasCredits: remaining > 0,
        remaining,
        total: FREE_TIER_LIMIT
      };
    } catch (error) {
      console.error('❌ Check credits error:', error);
      throw new Error('Failed to check user credits');
    }
  }
}

export default ResumeAnalysisService;
